import * as React from "react";
import * as Sentry from "@sentry/react";
import { getCurrentUser } from "@/lib/api/auth";
import { usePermissionStore } from "@/lib/stores/permission-store";

interface AuthUser {
  id: string;
  name: string;
  role: string;
  initials: string;
  hasSeenTour?: boolean;
}

interface AuthPharmacy {
  id: string;
  name: string;
  email?: string;
}

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  pharmacy: AuthPharmacy | null;
  email: string | null;
  emailVerified: boolean;
  needsFirstUser: boolean;
  needsLegalAcceptance: boolean;
  subscriptionStatus: string | null;
  error: string | null;
  refetch: () => Promise<void>;
}

const AuthContext = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<Omit<AuthState, "refetch">>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    pharmacy: null,
    email: null,
    emailVerified: false,
    needsFirstUser: false,
    needsLegalAcceptance: false,
    subscriptionStatus: null,
    error: null,
  });
  const fetchingRef = React.useRef(false);

  const fetchAuth = React.useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const data = await getCurrentUser();

      if (data?.error) {
        // USER_NOT_SELECTED means the pharmacy session is valid,
        // but no staff user has been selected yet — still authenticated
        const isPharmacyAuthenticated =
          data.code === "USER_NOT_SELECTED" && !!data.pharmacy;

        if (data.pharmacy) {
          Sentry.setTag("pharmacy_id", data.pharmacy.id);
          Sentry.setTag("pharmacy_name", data.pharmacy.name);
        }

        setState({
          isLoading: false,
          isAuthenticated: isPharmacyAuthenticated,
          user: null,
          pharmacy: data.pharmacy ?? null,
          email: data.email ?? null,
          emailVerified: data.emailVerified ?? false,
          needsFirstUser: data.needsFirstUser ?? false,
          needsLegalAcceptance: data.needsLegalAcceptance ?? false,
          subscriptionStatus: data.subscriptionStatus ?? null,
          error: isPharmacyAuthenticated ? null : data.error,
        });
        return;
      }

      usePermissionStore.getState().setRole(data?.user?.role ?? null);

      if (data?.pharmacy) {
        Sentry.setTag("pharmacy_id", data.pharmacy.id);
        Sentry.setTag("pharmacy_name", data.pharmacy.name);
      }
      if (data?.user) {
        Sentry.setUser({
          id: data.user.id,
          username: data.user.name,
        });
      }

      setState({
        isLoading: false,
        isAuthenticated: !!data?.pharmacy,
        user: data?.user ?? null,
        pharmacy: data?.pharmacy ?? null,
        email: data?.email ?? null,
        emailVerified: data?.emailVerified ?? false,
        needsFirstUser: data?.needsFirstUser ?? false,
        needsLegalAcceptance: data?.needsLegalAcceptance ?? false,
        subscriptionStatus: data?.subscriptionStatus ?? null,
        error: null,
      });
    } catch {
      usePermissionStore.getState().setRole(null);
      Sentry.setUser(null);

      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        pharmacy: null,
        email: null,
        emailVerified: false,
        needsFirstUser: false,
        needsLegalAcceptance: false,
        subscriptionStatus: null,
        error: "Failed to fetch auth state",
      });
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  const value = React.useMemo<AuthState>(
    () => ({ ...state, refetch: fetchAuth }),
    [state, fetchAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
